<template>
    <div class="">
        <div class="content pb-2">
            <div class="ppv2-py-4">
                <DataTable
                    :perPage="itemsPerPage"
                    :columns="tableColumns"
                    :items="subcategories.data"
                    :currentPage="currentPage"
                    :totalPages="totalPages"
                    :selectedItems="selectedItems"
                    :enableDragDrop="false"
                    :enableMultiSelect="true"
                    @page-changed="handlePageChange"
                    @item-selected="toggleItemSelection"
                    @select-all-toggled="toggleSelectAll"
                    @sort="onSort"
                >
                    <!-- Toolbar Slot -->
                    <template #toolbar>
                        <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                            <button
                                @click="$emit('back')"
                                class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-text-[#7D2DBD] hover:ppv2-underline"
                            >
                                <ChevronLeft :size="20" />
                                <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                                    {{ $t('experiences.actions.backToCategories') }}
                                </span>
                            </button>

                            <!-- Search Input -->
                            <div class="ppv2-relative">
                                <Search v-model="searchQuery" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                            </div>

                            <div class="ppv2-flex-1"></div>

                            <!-- Create Button -->
                            <BaseButton
                                variant="purple"
                                :custom-height="40"
                                :custom-font-size="14"
                                custom-class="ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px]"
                                @click="handleCreate"
                            >
                                <Plus :size="17" />
                                <span class="poppinsRegular">{{ $t('experiences.actions.create') }}</span>
                            </BaseButton>
                        </div>
                    </template>

                    <!-- Column: Category -->
                    <template #column-category="{ item }">
                        <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                            {{ item.category ? item.category.name : '-' }}
                        </span>
                    </template>

                    <!-- Column: Color -->
                    <template #column-color="{ item }">
                        <div
                            class="ppv2-w-6 ppv2-h-6 ppv2-rounded-full ppv2-border ppv2-border-gray-200"
                            :style="{ backgroundColor: item.color || '#ccc' }"
                        ></div>
                    </template>

                    <!-- Column: Visibility -->
                    <template #column-is_visible="{ item }">
                        <button
                            @click="toggleVisibility(item)"
                            class="ppv2-inline-flex ppv2-items-center ppv2-gap-2 ppv2-px-3 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-border-1"
                            :class="
                                item.is_visible
                                    ? 'ppv2-border-[#206A64] ppv2-bg-[#06B217] ppv2-bg-opacity-20'
                                    : 'ppv2-border-[#4E4E4E] ppv2-bg-[#474747] ppv2-bg-opacity-20'
                            "
                        >
                            <div
                                class="ppv2-w-9 ppv2-h-5 ppv2-rounded-full ppv2-flex ppv2-items-center"
                                :class="
                                    item.is_visible
                                        ? 'ppv2-bg-[#206A64] ppv2-justify-end'
                                        : 'ppv2-bg-[#4E4E4E] ppv2-justify-start'
                                "
                            >
                                <div class="ppv2-w-3.5 ppv2-h-3.5 ppv2-bg-white ppv2-rounded-full ppv2-mx-0.5"></div>
                            </div>
                            <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                                {{ item.is_visible ? $t('experiences.status.published') : $t('experiences.status.draft') }}
                            </span>
                        </button>
                    </template>

                    <!-- Column: Actions -->
                    <template #column-actions="{ item }">
                        <div
                            class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3"
                            :style="{ fontSize: normalizeFontSize(14) }"
                        >
                            <button
                                @click="handleEdit(item)"
                                class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                                :title="$t('experiences.actions.edit')"
                            >
                                <SquarePen :size="18" />
                            </button>
                            <button
                                class="btnTrash ppv2-p-1 hover:ppv2-bg-[#FF9C9C] hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                                :title="$t('experiences.actions.delete')"
                                @click="deleteSingleItem(item)"
                            >
                                <Trash2 :size="18" />
                            </button>
                        </div>
                    </template>

                    <!-- Bulk Actions Slot -->
                    <template #bulk-actions="{ selectedCount }">
                        <span class="poppinsRegular textGray" :style="{ fontSize: normalizeFontSize(15) }">
                            {{ $t('experiences.selection.itemsSelected', { count: selectedCount }) }}
                        </span>
                        <span
                            class="poppinsRegular ppv2-text-white ppv2-text-opacity-50"
                            :style="{ fontSize: normalizeFontSize(15) }"
                            >|</span
                        >
                        <BaseButton
                            variant="outline"
                            @click="deleteSelectedItems"
                            customClass="!ppv2-px-4 !ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                        >
                            <Trash2 :size="14" />
                            <span :style="{ fontSize: normalizeFontSize(14) }">{{ $t('common.deleteSelection') }}</span>
                        </BaseButton>
                    </template>
                </DataTable>
            </div>
        </div>

        <!-- Delete Confirmation Popup -->
        <CustomDialog v-model="showDeletePopup" :defaultTitle="deletePopupTitle" customClassTitle="poppinsSemiBold" noScroll>
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ deletePopupDescription }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    variant="outline"
                    @click="showDeletePopup = false"
                    customClass="!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]"
                    :customHeight="45"
                >
                    {{ $t('experiences.deletePopup.cancel') }}
                </BaseButton>
                <BaseButton
                    variant="danger"
                    @click="confirmDelete"
                    customClass="!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]"
                    :customHeight="45"
                >
                    {{ $t('experiences.deletePopup.confirm') }}
                </BaseButton>
            </template>
        </CustomDialog>

        <!-- Create Prestation Subcategory Popup -->
        <CustomDialog v-model="showCreatePopup" :defaultTitle="$t('experiences.createPopup.titleSub')" width="800px">
            <template #content>
                <PrestationSubcategoryForm :initialCategoryId="categoryId" @success="handleCreateSuccess" />
            </template>
            <template #actions>
                <span></span>
            </template>
        </CustomDialog>

        <!-- Edit Prestation Subcategory Popup -->
        <CustomDialog v-model="showEditPopup" :defaultTitle="$t('experiences.createPopup.titleUpdateSub')" width="800px">
            <template #content>
                <PrestationSubcategoryForm v-if="showEditPopup" :editItem="editingItem" @success="handleEditSuccess" />
            </template>
            <template #actions>
                <span></span>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import { Plus, Trash2, SquarePen, ChevronLeft } from 'lucide-vue'
import normalizeSize from '../../../utils/normalizeSize'
import Search from './components/Search.vue'
import PrestationSubcategoryForm from './components/PrestationSubcategoryForm.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import DataTable from '../../Shared/DataTable.vue'
import { eventBus } from '../../Customer/eventBus'
import axios from 'axios'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../../utils/perPage'

export default {
    name: 'PrestationsSubcategoriesTable',
    components: {
        Plus,
        SquarePen,
        Trash2,
        ChevronLeft,
        Search,
        BaseButton,
        CustomDialog,
        DataTable,
        PrestationSubcategoryForm,
    },
    props: {
        categoryId: {
            type: [Number, String],
            default: null,
        },
    },
    data() {
        return {
            searchQuery: '',
            currentPage: 1,
            itemsPerPage: getPerPage('prestation_subcategories_items_per_page', 10),
            selectedItems: [],
            isLoading: false,
            error: null,
            tableColumns: [
                { key: 'name', label: this.$t('experiences.table.name'), sortable: true, type: 'text' },
                { key: 'category', label: this.$t('experiences.table.category'), sortable: false, type: 'text' },
                { key: 'color', label: this.$t('experiences.table.color'), sortable: false, type: 'text' },
                { key: 'actions', label: this.$t('experiences.table.actions'), sortable: false, type: 'actions' },
            ],
            showDeletePopup: false,
            deletePopupTitle: this.$t('experiences.deletePopup.title'),
            deletePopupDescription: '',
            deleteAction: null, // 'single' or 'multiple'
            itemToDelete: null,
            subcategories: {
                data: [],
                total: 0,
            },
            sortKey: 'id',
            sortOrder: 'desc',
            showCreatePopup: false,
            showEditPopup: false,
            editingItem: null,
        }
    },
    computed: {
        totalPages() {
            return this.subcategories.total
        },
    },
    mounted() {
        eventBus.$on('dt-selected-perpage-changed', this.handlePerPageChange)
        this.fetchSubcategories()
    },
    beforeDestroy() {
        eventBus.$off('dt-selected-perpage-changed', this.handlePerPageChange)
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        async fetchSubcategories() {
            this.isLoading = true
            try {
                const response = await axios.get('/api/v3/prestation_subcategory/list', {
                    params: {
                        currentPage: this.currentPage,
                        itemsPerPage: this.itemsPerPage,
                        search: this.searchQuery,
                        orderBy: this.sortKey,
                        orderDirection: this.sortOrder,
                        category_id: this.categoryId,
                    },
                })
                this.subcategories = {
                    data: response.data.data,
                    total: response.data.last_page,
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.isLoading = false
            }
        },
        async toggleVisibility(item) {
            const previousVisibility = item.is_visible
            item.is_visible = !item.is_visible
            try {
                await axios.patch('/api/v3/prestation_subcategory/update-visibility', {
                    id: item.id,
                    type: 'subcategory',
                    is_visible: item.is_visible,
                })
            } catch (error) {
                this.$toast.error(error.response.data.message)
                item.is_visible = previousVisibility
                console.error('Error updating visibility:', error)
            }
        },
        handlePageChange(page) {
            this.currentPage = page
            this.fetchSubcategories()
        },
        handlePerPageChange(perPage) {
            this.itemsPerPage = Number(perPage)
            setPerPage('prestation_subcategories_items_per_page', this.itemsPerPage)
            this.currentPage = 1
            this.fetchSubcategories()
        },
        handleSearchUpdate(searchValue) {
            this.searchQuery = searchValue
            this.currentPage = 1
            this.fetchSubcategories()
        },
        toggleItemSelection(itemId) {
            const index = this.selectedItems.indexOf(itemId)
            if (index > -1) {
                this.selectedItems.splice(index, 1)
            } else {
                this.selectedItems.push(itemId)
            }
        },
        toggleSelectAll() {
            if (this.selectedItems.length === this.subcategories.data.length) {
                this.selectedItems = []
            } else {
                this.selectedItems = this.subcategories.data.map((item) => item.id)
            }
        },
        deleteSingleItem(item) {
            this.deleteAction = 'single'
            this.itemToDelete = item
            this.deletePopupDescription = this.$t('experiences.deletePopup.message')
            this.showDeletePopup = true
        },
        deleteSelectedItems() {
            this.deleteAction = 'multiple'
            this.deletePopupDescription = this.$t('experiences.deletePopup.message')
            this.showDeletePopup = true
        },
        async confirmDelete() {
            try {
                if (this.deleteAction === 'single') {
                    await axios.delete(`/api/v3/prestation_subcategory/delete/${this.itemToDelete.id}`)
                } else {
                    await axios.delete('/api/v3/prestation_subcategory/bulk-delete', {
                        data: { ids: this.selectedItems },
                    })
                }
                this.fetchSubcategories()
                this.selectedItems = []
                this.$emit('refresh-stats')
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error deleting subcategory:', error)
                if (error.response && error.response.data && error.response.data.message) {
                    alert(error.response.data.message)
                }
            } finally {
                this.showDeletePopup = false
                this.deleteAction = null
                this.itemToDelete = null
            }
        },
        onSort(key) {
            if (this.sortKey === key) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortKey = key
                this.sortOrder = 'asc'
            }
            this.currentPage = 1
            this.fetchSubcategories()
        },
        handleCreate() {
            this.showCreatePopup = true
        },
        handleCreateSuccess() {
            this.showCreatePopup = false
            this.fetchSubcategories()
            this.$emit('refresh-stats')
        },
        handleEdit(item) {
            this.editingItem = item
            this.showEditPopup = true
        },
        handleEditSuccess() {
            this.showEditPopup = false
            this.editingItem = null
            this.fetchSubcategories()
            this.$emit('refresh-stats')
        },
    },
}
</script>

<style scoped lang="scss">
.textGray {
    color: #1a1f37;
}
.btnTrash {
    color: #911414;
}
.dark {
    .textGray {
        color: #bfbfbf;
    }
    .btnTrash {
        color: #ff9c9c;
    }
}
</style>
